import { yup } from '@jbc-year-end-adj/common/yup';
import { Premium } from './query';
import { FileType } from '@jbc-year-end-adj/2024/types';
import { amountFormat, fileSchemaFormat } from '@jbc-year-end-adj/2024/utils/formatter';

export type Schema = {
  corporatePension?: string;
  corporatePensionImage?: FileType | File | null;
  disabilityMutualAid?: string;
  disabilityMutualAidImage?: FileType | File | null;
  personalPension?: string;
  personalPensionImage?: FileType | File | null;
  smallAndMediumSizedEnterprises?: string;
  smallAndMediumSizedEnterprisesImage?: FileType | File | null;
};

const isPremiumAmount = (smallAndMediumSizedEnterprises: string) => {
  const amount = Number(smallAndMediumSizedEnterprises.split(',').join(''));
  return amount !== 0;
};

export const premiumSchema = (imageRequired: boolean) => {
  return yup.object({
    smallAndMediumSizedEnterprises: yup
      .string()
      .positiveAmountFormat()
      .maxAmount(999999999)
      .label('独立行政法人中小企業基盤整備機構の共済契約の掛金'),
    smallAndMediumSizedEnterprisesImage: imageRequired
      ? yup.mixed<FileType | File>().when(['smallAndMediumSizedEnterprises'], {
          is: isPremiumAmount,
          then: schema =>
            schema
              .fileSize()
              .required()
              .label('独立行政法人中小企業基盤整備機構の共済契約の掛金 証明書画像'),
          otherwise: schema =>
            schema
              .fileSize()
              .nullable()
              .label('独立行政法人中小企業基盤整備機構の共済契約の掛金 証明書画像')
        })
      : yup
          .mixed<FileType | File>()
          .fileSize()
          .nullable()
          .label('独立行政法人中小企業基盤整備機構の共済契約の掛金 証明書画像'),
    corporatePension: yup
      .string()
      .positiveAmountFormat()
      .maxAmount(999999999)
      .label('確定拠出年金法に規定する企業型年金加入者掛金'),
    corporatePensionImage: imageRequired
      ? yup.mixed<FileType | File>().when(['corporatePension'], {
          is: isPremiumAmount,
          then: schema =>
            schema
              .fileSize()
              .required()
              .label('確定拠出年金法に規定する企業型年金加入者掛金 証明書画像'),
          otherwise: schema =>
            schema
              .fileSize()
              .nullable()
              .label('確定拠出年金法に規定する企業型年金加入者掛金 証明書画像')
        })
      : yup
          .mixed<FileType | File>()
          .fileSize()
          .nullable()
          .label('確定拠出年金法に規定する企業型年金加入者掛金 証明書画像'),
    personalPension: yup
      .string()
      .positiveAmountFormat()
      .maxAmount(999999999)
      .label('確定拠出年金法に規定する個人型年金加入者掛金'),
    personalPensionImage: imageRequired
      ? yup.mixed<FileType | File>().when(['personalPension'], {
          is: isPremiumAmount,
          then: schema =>
            schema
              .fileSize()
              .required()
              .label('確定拠出年金法に規定する個人型年金加入者掛金 証明書画像'),
          otherwise: schema =>
            schema
              .fileSize()
              .nullable()
              .label('確定拠出年金法に規定する個人型年金加入者掛金 証明書画像')
        })
      : yup
          .mixed<FileType | File>()
          .fileSize()
          .nullable()
          .label('確定拠出年金法に規定する個人型年金加入者掛金 証明書画像'),
    disabilityMutualAid: yup
      .string()
      .positiveAmountFormat()
      .maxAmount(999999999)
      .label('心身障害者扶養共済制度に関する契約の掛金'),
    disabilityMutualAidImage: imageRequired
      ? yup.mixed<FileType | File>().when(['disabilityMutualAid'], {
          is: isPremiumAmount,
          then: schema =>
            schema
              .fileSize()
              .required()
              .label('心身障害者扶養共済制度に関する契約の掛金 証明書画像'),
          otherwise: schema =>
            schema
              .fileSize()
              .nullable()
              .label('心身障害者扶養共済制度に関する契約の掛金 証明書画像')
        })
      : yup
          .mixed<FileType | File>()
          .fileSize()
          .nullable()
          .label('心身障害者扶養共済制度に関する契約の掛金 証明書画像')
  });
};

export const generateDefaultValues = (premium: Premium | undefined): Schema => {
  return {
    smallAndMediumSizedEnterprises: amountFormat(premium?.smallAndMediumSizedEnterprises),
    smallAndMediumSizedEnterprisesImage: fileSchemaFormat(premium?.smallAndMediumSizedEnterprisesImage),
    corporatePension: amountFormat(premium?.corporatePension),
    corporatePensionImage: fileSchemaFormat(premium?.corporatePensionImage),
    personalPension: amountFormat(premium?.personalPension),
    personalPensionImage: fileSchemaFormat(premium?.personalPensionImage),
    disabilityMutualAid: amountFormat(premium?.disabilityMutualAid),
    disabilityMutualAidImage: fileSchemaFormat(premium?.disabilityMutualAidImage)
  };
};
